import { Children, useMemo, useState } from "react";
import Box from "@mui/material/Box";
import {
  Button,
  Collapse,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import Icon from "../../../../Icon/Icon";
import { TabContext, TabPanel } from "@mui/lab";
import { useConstants } from "../Main/useConstants";
import { useTranslation } from "react-i18next";

const transition = "all 200ms linear";

// Tab "carpeta" para una sección de la investigación
const FolderSection = ({
  tabProps,
  index,
  childrenQty,
  activeFolder,
  handleChange,
  isMobile,
  isCompact,
}) => {
  const { key, name, icon, iconSelected, color } = tabProps;
  const isActive = activeFolder === key;
  const theme = useTheme();

  const [isHovering, setIsHovering] = useState(false);

  // En mobile + activo: permitir 2 líneas con elipsis dentro del alto fijo del tab
  const isMobileActive = isMobile && isActive;

  return (
    <Button
      key={key}
      onClick={() => handleChange(key)}
      onMouseEnter={() => setIsHovering(true)}
      onMouseLeave={() => setIsHovering(false)}
      disableRipple
      sx={{
        "&": {
          padding: 0,
          marginRight: isCompact ? "-10px" : "-15px",
          fontSize: "14px",
          fontWeight: "bold",
          position: "relative",
          zIndex: isActive ? childrenQty - index + 2 : childrenQty - index,
          cursor: "pointer",
          height: isActive ? "45px" : "37px",
          transition,
          flexShrink: 0,
        },
        display: "flex",
        alignItems: "flex-end",
        "&:hover": {
          backgroundColor: "transparent",
        },
      }}
    >
      <Box
        sx={{
          padding: isMobileActive
            ? "5px 10px"
            : isActive
            ? isCompact
              ? "10px 8px"
              : "10px 10px"
            : isCompact
            ? "10px 4px 10px 12px"
            : "10px 5px 10px 15px",
          height: isActive ? "45px" : "37px",
          display: "flex",
          border: `2px solid ${color}`,
          borderBottom: "none",
          borderRadius: "15px 3px 0 0",
          borderRight: "transparent",
          backgroundColor: isActive ? color : "#FFFFFF",
          alignItems: "center",
          transition,
        }}
      >
        <Icon
          name={isActive ? iconSelected : icon}
          color={isActive ? theme.palette.common.white : color}
          size={isActive ? (isMobileActive ? 20 : 24) : 16}
          style={{ marginTop: !isActive ? 2 : 0, flexShrink: 0 }}
        />
        <Collapse orientation="horizontal" in={isActive || isHovering}>
          <Typography
            fontWeight={600}
            textTransform="none"
            transition="all 0.25s linear"
            opacity={isActive ? 1 : 0}
            color={isActive ? theme.palette.common.white : color}
            marginLeft={isActive ? 1 : 0.5}
            sx={
              isMobileActive
                ? {
                    fontSize: "11px",
                    lineHeight: 1.15,
                    maxWidth: 140,
                    display: "-webkit-box",
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: "vertical",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "normal",
                    textAlign: "left",
                  }
                : {
                    fontSize: isActive ? "14px" : "12px",
                    overflow: "hidden",
                    whiteSpace: "nowrap",
                  }
            }
          >
            {name[0].toUpperCase() + name.slice(1)}
          </Typography>
        </Collapse>
      </Box>
      <Box
        sx={{
          position: "relative",
          height: isActive ? 45 : 37,
          backgroundColor: "transparent",
          border: "none",
          transition,
        }}
      >
        <Box // Tab
          sx={{
            position: "absolute",
            width: "12px",
            left: "2px",
            height: "calc(100%)",
            backgroundColor: isActive ? color : theme.palette.common.white,
            borderTop: `2px solid ${color}`,
            borderRight: `2px solid ${color}`,
            transform: "skewX(15deg)",
            borderTopRightRadius: "20px",
            zIndex: 2,
            boxShadow: "2px 0px 2px 0px rgba(0, 0, 0, 0.2)",
            transition,
          }}
        />
        <Box // Triangle
          sx={{
            position: "absolute",
            top: isActive ? "1px" : "2px",
            width: "13px",
            left: "-1px",
            height: isActive ? "calc(100%)" : "calc(100% - 2px)",
            backgroundColor: isActive ? color : theme.palette.common.white,
            zIndex: 2,
            clipPath: "polygon(0 0, 50% 30%, 100% 100%, 0 100%);",
            transition,
          }}
        />
        <Box // Spacing
          sx={{
            position: "relative",
            width: isCompact ? "16px" : "28px",
            backgroundColor: "transparent",
            border: "none",
          }}
        />
      </Box>
    </Button>
  );
};

// Botón "más" + Menú con secciones que no están activas (mobile)
const OverflowMenu = ({ tabs, activeFolder, handleChange, color }) => {
  const [t] = useTranslation("investigation");
  const theme = useTheme();
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  const otherTabs = tabs.filter((tab) => tab.key !== activeFolder);
  if (otherTabs.length === 0) return null;

  const label = t("sections.more_sections");

  return (
    <>
      <IconButton
        onClick={(e) => setAnchorEl(e.currentTarget)}
        aria-label={label}
        title={label}
        size="small"
        sx={{
          height: 37,
          width: 37,
          ml: "auto",
          border: `2px solid ${color}`,
          borderRadius: "10px",
          backgroundColor: "#FFFFFF",
          alignSelf: "flex-end",
          "&:hover": {
            backgroundColor: theme.palette.action.hover,
          },
        }}
      >
        <Icon name="outline_menu_board" color={color} size={20} />
      </IconButton>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      >
        {otherTabs.map((tab) => (
          <MenuItem
            key={tab.key}
            onClick={() => {
              handleChange(tab.key);
              setAnchorEl(null);
            }}
          >
            <ListItemIcon>
              <Icon name={tab.icon} color={tab.color} size={20} />
            </ListItemIcon>
            <ListItemText
              primary={tab.name[0].toUpperCase() + tab.name.slice(1)}
              primaryTypographyProps={{
                fontWeight: 600,
                fontSize: "14px",
                color: tab.color,
              }}
            />
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};

// Predicado: ¿esta tab debe ocultarse según el tipo de investigación?
const shouldHideTab = (tabKey, advance, enums) => {
  const allowOnSimple = [
    enums.sections.researchFolder,
    enums.sections.caseEvaluation,
    enums.sections.involveds,
    enums.sections.evidences,
    enums.sections.financialImpact,
    enums.sections.conclusion,
  ];

  return !advance && !allowOnSimple.includes(tabKey);
};

export const InvestigationNavigation = ({
  activeFolder,
  advance,
  hdlSelect,
  children,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const isBelowLg = useMediaQuery(theme.breakpoints.down("lg"));
  // Rango "tablet/laptop chica" (≈ 900–1200px): tabs visibles pero compactas
  const isCompactDesktop = isBelowLg && !isMobile;

  const { enums } = useConstants();

  // Tabs visibles (filtradas por tipo de investigación)
  const visibleTabs = useMemo(() => {
    const tabs = [];
    Children.forEach(children, (child) => {
      const tabProps = child?.props?.tabProps;
      if (!tabProps) return;
      if (shouldHideTab(tabProps.key, advance, enums)) return;
      tabs.push(tabProps);
    });
    return tabs;
  }, [children, advance, enums]);

  // Color de la sección seleccionada (cae a un default cuando no hay match)
  const selectedSectionColor = useMemo(() => {
    const found = visibleTabs.find((tab) => tab.key === activeFolder);
    return found?.color ?? theme.palette.blueGray.main;
  }, [visibleTabs, activeFolder, theme]);

  const activeTab = visibleTabs.find((tab) => tab.key === activeFolder);

  return (
    <Box className="investigation-navigation" marginTop={1}>
      <Stack
        direction="row"
        flexWrap="nowrap"
        alignItems="end"
        justifyContent="space-between"
        height={45}
        zIndex={0}
        gap={1}
      >
        <Stack
          direction="row"
          flexWrap="nowrap"
          alignItems="end"
          height={45}
          zIndex={0}
          minWidth={0}
          flex={1}
        >
          {isMobile ? (
            <>
              {activeTab && (
                <FolderSection
                  tabProps={activeTab}
                  index={0}
                  childrenQty={1}
                  activeFolder={activeFolder}
                  handleChange={hdlSelect}
                  isMobile
                />
              )}
              <OverflowMenu
                tabs={visibleTabs}
                activeFolder={activeFolder}
                handleChange={hdlSelect}
                color={selectedSectionColor}
              />
            </>
          ) : (
            visibleTabs.map((tab, index) => (
              <FolderSection
                key={tab.key}
                tabProps={tab}
                index={index}
                childrenQty={visibleTabs.length}
                activeFolder={activeFolder}
                handleChange={hdlSelect}
                isCompact={isCompactDesktop}
              />
            ))
          )}
        </Stack>
      </Stack>

      <TabContext value={activeFolder}>
        <Box
          sx={{
            border: `2px solid ${selectedSectionColor}`,
            borderRadius: "0 0 10px 10px",
            overflow: "hidden",
            transition,
          }}
        >
          {Children.map(children, (child) => {
            const tabProps = child?.props?.tabProps;
            if (!tabProps) return null;
            if (shouldHideTab(tabProps.key, advance, enums)) return null;
            return (
              <TabPanel
                key={tabProps.key}
                value={tabProps.key}
                sx={{ padding: 0 }}
              >
                {child}
              </TabPanel>
            );
          })}
        </Box>
      </TabContext>
    </Box>
  );
};
